import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import type { AnalysisStep, AnalysisResult } from "@/types/analysis";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { usePageMeta } from "@/hooks/usePageMeta";

const steps: { key: AnalysisStep; label: string }[] = [
  { key: "crawling", label: "Crawling page content" },
  { key: "seo-audit", label: "Running SEO audit" },
  { key: "ai-visibility", label: "Analyzing AI visibility" },
  { key: "backlinks", label: "Evaluating backlink profile" },
  { key: "competitors", label: "Benchmarking competitors" },
  { key: "predictions", label: "Predicting ranking potential" },
  { key: "action-plan", label: "Generating action plan" },
];

const AnalyzePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const url = searchParams.get("url") || "";
  const email = searchParams.get("email") || "";

  usePageMeta({
    title: url ? `Analyzing ${url} — Unranked` : "Analyzing — Unranked",
    description: url ? `Running AI-powered SEO analysis for ${url}.` : "Running AI-powered SEO analysis.",
    canonical: "https://unrankedagency.com/analyze",
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  useEffect(() => {
    if (!url || !email) {
      navigate("/");
      return;
    }
    runAnalysis();
  }, [url, email]);

  const runAnalysis = async () => {
    try {
      // Step 1: Crawl
      setCurrentStep(0);
      const crawlController = new AbortController();
      const crawlTimeout = setTimeout(() => crawlController.abort(), 90_000);
      // @ts-ignore - signal supported but not in types
      const { data: crawlData, error: crawlError } = await supabase.functions.invoke("crawl-url", {
        body: { url, email },
        signal: crawlController.signal,
      });
      clearTimeout(crawlTimeout);
      if (crawlError) throw new Error(crawlError.message);
      if (!crawlData?.success) throw new Error(crawlData?.error || "Failed to crawl page");
      setCompletedSteps([0]);

      // Step 2-7: AI Analysis
      setCurrentStep(1);
      const analyzeController = new AbortController();
      const analyzeTimeout = setTimeout(() => analyzeController.abort(), 180_000);
      // @ts-ignore - signal supported but not in types
      const { data: analysisData, error: analysisError } = await supabase.functions.invoke("analyze-seo", {
        body: { url, crawlData: crawlData.data, email },
        signal: analyzeController.signal,
      });
      clearTimeout(analyzeTimeout);
      if (analysisError) throw new Error(analysisError.message);
      if (!analysisData) throw new Error("No analysis data returned");

      // Attach crawled markdown for Content tab (zero-cost reuse)
      if (crawlData.data?.markdown) {
        analysisData.pageContent = crawlData.data.markdown;
      }

      // Simulate step progression for UX
      for (let i = 1; i <= 6; i++) {
        setCurrentStep(i);
        setCompletedSteps((prev) => [...prev, i]);
        await new Promise((r) => setTimeout(r, 400));
      }

      // Save to DB if user is authenticated
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData?.session?.user) {
        await supabase.from("analyses").upsert(
          { user_id: sessionData.session.user.id, url, results: analysisData },
          { onConflict: "user_id,url" }
        );
      }

      // Navigate to report
      const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(analysisData))));
      navigate(`/report?data=${encodeURIComponent(encoded)}&email=${encodeURIComponent(email)}`);
    } catch (err: any) {
      console.error("Analysis error:", err);
      setError(err.message || "An unexpected error occurred");
      toast({
        title: "Analysis failed",
        description: err.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,hsl(160_84%_45%/0.05),transparent_60%)]" />

      <div className="relative z-10 w-full max-w-lg mx-auto px-6">
        <div className="text-center mb-10">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Search className="w-6 h-6 text-primary" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Analyzing Your Website</h1>
          <p className="text-sm text-muted-foreground truncate max-w-sm mx-auto">{url}</p>
        </div>

        <div className="space-y-3">
          {steps.map((step, i) => {
            const isComplete = completedSteps.includes(i);
            const isCurrent = currentStep === i && !error;
            const isPending = !isComplete && !isCurrent;
            const isFailed = error && currentStep === i;

            return (
              <div
                key={step.key}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ${
                  isComplete
                    ? "bg-success/10 border border-success/20"
                    : isCurrent
                    ? "bg-primary/10 border border-primary/20"
                    : isFailed
                    ? "bg-destructive/10 border border-destructive/20"
                    : "bg-card/50 border border-border/50 opacity-50"
                }`}
              >
                {isComplete ? (
                  <CheckCircle2 className="w-5 h-5 text-success shrink-0" />
                ) : isCurrent ? (
                  <Loader2 className="w-5 h-5 text-primary shrink-0 animate-spin" />
                ) : isFailed ? (
                  <AlertCircle className="w-5 h-5 text-destructive shrink-0" />
                ) : (
                  <div className="w-5 h-5 rounded-full border border-border shrink-0" />
                )}
                <span
                  className={`text-sm font-medium ${
                    isComplete
                      ? "text-success"
                      : isCurrent
                      ? "text-foreground"
                      : isFailed
                      ? "text-destructive"
                      : "text-muted-foreground"
                  }`}
                >
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>

        {!error && (
          <p className="mt-8 text-center text-sm text-muted-foreground animate-pulse">
            This usually takes 1–2 minutes. Hang tight!
          </p>
        )}

        {error && (
          <div className="mt-6 text-center">
            <p className="text-sm text-destructive mb-4">{error}</p>
            <button
              onClick={() => navigate("/")}
              className="text-sm text-primary hover:underline"
            >
              ← Try a different URL
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyzePage;
